const A = 1
const C = 2
const G = 4
const T = 8

/** Inosine pairs with any base, so primer tables write it as `I` where the IUPAC alphabet has `N`. */
const IUPAC_MASKS: Record<string, number> = {
  A,
  C,
  G,
  T,
  U: T,
  R: A | G,
  Y: C | T,
  S: C | G,
  W: A | T,
  K: G | T,
  M: A | C,
  B: C | G | T,
  D: A | G | T,
  H: A | C | T,
  V: A | C | G,
  N: A | C | G | T,
  I: A | C | G | T
}

const COMPLEMENT: Record<string, string> = {
  A: 'T',
  C: 'G',
  G: 'C',
  T: 'A',
  U: 'A',
  R: 'Y',
  Y: 'R',
  S: 'S',
  W: 'W',
  K: 'M',
  M: 'K',
  B: 'V',
  V: 'B',
  D: 'H',
  H: 'D',
  N: 'N',
  I: 'I'
}

const MASK_TO_CODE: Record<number, string> = Object.fromEntries(
  Object.entries(IUPAC_MASKS)
    .filter(([code]) => code !== 'U' && code !== 'I')
    .map(([code, mask]) => [mask, code])
)

/** A primer, cleaned and upper-cased: IUPAC codes and inosine, at least 12 bases. */
export const IUPAC_PRIMER = /^[ACGTRYSWKMBDHVNI]{12,}$/

export const maskToIupac = (masks: readonly number[]): string => masks.map((mask) => MASK_TO_CODE[mask] ?? 'N').join('')

export const cleanPrimer = (primer: string): string => primer.toUpperCase().replace(/[^A-Z]/g, '')

export const reverseComplement = (sequence: string): string =>
  cleanPrimer(sequence)
    .split('')
    .reverse()
    .map((base) => COMPLEMENT[base] ?? 'N')
    .join('')

export const toMasks = (sequence: string): number[] =>
  cleanPrimer(sequence)
    .split('')
    .map((base) => IUPAC_MASKS[base] ?? 0)

const READ_MASKS: Record<string, number> = { A, C, G, T, U: T }

/** A read's `N` matches no primer base, as in cutadapt: N-heavy tails would otherwise carry any primer. */
export const readMask = (base: string): number => READ_MASKS[base] ?? 0

/**
 * Smallest edit distance of the whole primer against any read substring starting within
 * `[0, maxStart]` — the start is free inside that window (heterogeneity spacers, leftover index
 * bases), the primer must align end to end. Indels count, because Nanopore errors are mostly indels.
 * With `cutoff`, gives up as soon as no alignment can stay within it and reports `cutoff + 1`.
 */
export const bestPrefixEditDistance = (primer: number[], read: string, maxStart: number, cutoff = Number.POSITIVE_INFINITY): number => {
  const m = primer.length
  const n = Math.min(read.length, maxStart + m + Math.ceil(m / 4))
  if (m === 0) {
    return 0
  }
  const big = m + n + 1
  let prev = new Int32Array(n + 1)
  let cur = new Int32Array(n + 1)
  const bases = new Int32Array(n)
  for (let j = 0; j < n; j++) {
    bases[j] = readMask(read[j])
  }
  for (let j = 0; j <= n; j++) {
    prev[j] = j <= maxStart ? 0 : big
  }
  for (let i = 1; i <= m; i++) {
    cur[0] = i
    let rowMin = i
    const p = primer[i - 1]
    for (let j = 1; j <= n; j++) {
      let best = prev[j - 1] + ((p & bases[j - 1]) !== 0 ? 0 : 1)
      if (prev[j] + 1 < best) {
        best = prev[j] + 1
      }
      if (cur[j - 1] + 1 < best) {
        best = cur[j - 1] + 1
      }
      cur[j] = best
      if (best < rowMin) {
        rowMin = best
      }
    }
    if (rowMin > cutoff) {
      return cutoff + 1
    }
    const swap = prev
    prev = cur
    cur = swap
  }
  let distance = big
  for (let j = 1; j <= n; j++) {
    if (prev[j] < distance) {
      distance = prev[j]
    }
  }
  return distance
}
